import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { api, apiBlob, ApiError } from '../../lib/api';
import { dataUrlToBlob, encodePhoto } from '../../lib/photo';
import { keys } from '../../lib/queries';
import { useT } from '../../i18n';

interface Summary { people: number; relationships: number; skipped: { what: string; reason: string }[] }

export function ImportExport({ treeId, treeName, isOwner }: { treeId: string; treeName: string; isOwner: boolean }) {
  const t = useT().io;
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [progress, setProgress] = useState('');
  const [summary, setSummary] = useState<(Summary & { photos: number }) | null>(null);
  const [error, setError] = useState('');

  const exportZip = async () => {
    try {
      const blob = await apiBlob(`/trees/${treeId}/export`);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${treeName.replace(/[^\w-]+/g, '-')}.zip`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.exportFailed);
    }
  };

  const importFile = async (file: File) => {
    setError('');
    setSummary(null);
    try {
      const backup = JSON.parse(await file.text()) as {
        app?: string;
        tree?: { people?: Record<string, { photoData?: string }> };
        photos?: Record<string, string>;
      };
      const count = Object.keys(backup.tree?.people ?? {}).length;
      setProgress(t.importing(count));
      const res = await api<{ summary: Summary; idMap: Record<string, string> }>('POST', `/trees/${treeId}/import`, backup);
      // Photos go through the normal upload endpoint (thumbnails are generated here, on the device).
      const entries = Object.keys(res.idMap)
        .map((old) => ({ id: res.idMap[old]!, data: backup.photos?.[old] ?? backup.tree?.people?.[old]?.photoData }))
        .filter((e): e is { id: string; data: string } => Boolean(e.data?.startsWith('data:image/')));
      let done = 0;
      for (const e of entries) {
        setProgress(t.importingPhotos(count, entries.length, done));
        try {
          const enc = await encodePhoto(await dataUrlToBlob(e.data));
          const form = new FormData();
          form.set('full', new File([enc.full], 'p.jpg', { type: 'image/jpeg' }));
          form.set('thumb', new File([enc.thumb], 'p_256.jpg', { type: 'image/jpeg' }));
          const { id } = await api<{ id: string }>('POST', `/trees/${treeId}/photos`, form);
          await api('PATCH', `/trees/${treeId}/people/${e.id}`, { photoId: id });
          done++;
        } catch {
          res.summary.skipped.push({ what: t.aPhoto, reason: t.couldntProcess });
        }
      }
      setSummary({ ...res.summary, photos: done });
      await qc.invalidateQueries({ queryKey: keys.tree(treeId) });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t.couldntImport);
    } finally {
      setProgress('');
    }
  };

  return (
    <div className="stack">
      <h2 style={{ marginTop: 16 }}>{t.title}</h2>
      <div className="row">
        <button className="btn" onClick={exportZip}>{t.export}</button>
        {isOwner && <button className="btn" onClick={() => input.current?.click()}>{t.import}</button>}
      </div>
      <input ref={input} type="file" accept="application/json,.json" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void importFile(f); e.target.value = ''; }} />
      {progress && <div className="banner" role="status">{progress}</div>}
      {error && <div className="error" role="alert">{error}</div>}
      {summary && (
        <div className="banner" role="status">
          {t.done(summary.people, summary.relationships, summary.photos)}
          {summary.skipped.length > 0 && (
            <ul>{summary.skipped.map((s, i) => <li key={i}>{t.skipped(s.what, s.reason)}</li>)}</ul>
          )}
          {t.undoHint}
        </div>
      )}
    </div>
  );
}
